import { useCallback, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { CiSearch } from "react-icons/ci";
import { FaUsers } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useFetch } from "../../hooks/useFetch";
import { isReportsPath } from "../../lib/routeTokens";
import { hasRoles, showToast } from "../../lib/utility";
import { tableCustomStyles } from "../../lib/utils";
import { middleware } from "../../redux/store";
import { authSelector } from "../Auth/AuthSlice";
import { userListGet, userSelector } from "../User/UserSlice";
import CloseButton from "./CloseButton";
import DataTablePaginationComponent from "./DataTablePaginationComponent";
import FormEnter from "./FormEnter";
import SkeletonComponent from "./SkeletonComponent";
import Modal from "./Modal";
import "./userFilter.scss";

const UserFilter = ({
  modalClose,
  onApplyFilter,
  onNextClick,
  userModalParams,
  showShelfName = false,
}) => {
  const { t } = useTranslation("common");
  const [searchVal, setSearchVal] = useState("");
  const [usersList, setUsersList] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState(usersList);

  const { user } = useSelector(authSelector);
  const location = useLocation();
  const {
    onlyUserSelect,
    canSelectUsers,
    multipleUsers,
    ifId,
    allSelectedText,
    ifNotAll,
    allSelected,
  } = userModalParams;

  const isSupers =
    canSelectUsers === undefined
      ? hasRoles("admin,superadmin,system,seller,manager", user) &&
        isReportsPath(location)
      : canSelectUsers;
  const isSellerOrManager = hasRoles("seller,manager", user);
  const canShowSelfName = hasRoles("admin,system", user);
  const { isFetchingUserList } = useSelector(userSelector);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const dispatch = useDispatch();
  const data = useCallback(filteredUsers, [filteredUsers, usersList]);
  const columns = [
    {
      name: t("username", { defaultValue: "Username" }),
      selector: (row) =>
        (isSellerOrManager ? row.userEntry.systemId : row.systemId) ?? "-",
      sortable: true,
    },
    {
      name: t("masterId", { defaultValue: "Master Id" }),
      selector: (row) =>
        (isSellerOrManager ? row.userEntry.masterId : row.masterId) ?? "-",
      sortable: true,
    },
    {
      name: t("currency", { defaultValue: "Currency" }),
      selector: (row) =>
        (isSellerOrManager ? row.userEntry.currency : row.currency) ?? "-",
      sortable: true,
    },
  ];

  const [selectUsers, setSelectUsers] = useState({
    allowed: isSupers,
    userIds: "",
    next: false,
    allSelected: false,
  });
  const [clearSelectedRows, setClearSelectedRows] = useState(false);

  const getEmptySelection = () => ({
    allowed: isSupers,
    userIds: "",
    next: false,
    allSelected: false,
  });

  const handleUserSelectionReset = () => {
    const emptySelection = getEmptySelection();
    setSelectUsers(emptySelection);
    setClearSelectedRows(true);
    onNextClick?.(emptySelection);
    setTimeout(() => setClearSelectedRows(false), 100);
  };
  const handleSelectedUserChange = ({ selectedCount, selectedRows }) => {
    const allSelected = selectedCount === usersList.length;
    const isOnlyOneUser = usersList.length === 1;

    if (!multipleUsers && !allSelected && selectedCount > 1) {
      showToast(
        t("userFilter.onlyOneUser", { defaultValue: "Can only select 1 user" }),
        "warning"
      );
    }

    const selectedIds = selectedRows
      .map((sr) =>
        isSellerOrManager
          ? sr.userEntry[ifId ? "id" : "systemId"]
          : sr[ifId ? "id" : "systemId"]
      )
      .join(",");

    setSelectUsers((prev) => ({
      ...prev,
      userIds:
        allSelected && !isOnlyOneUser
          ? ifNotAll
            ? selectedIds
            : allSelectedText
          : selectedIds,
      allSelected,
    }));
  };
  const handleNextClick = () => {
    if (onlyUserSelect) {
      onApplyFilter({
        clientId: selectUsers.userIds,
        allSelected: usersList.length === selectUsers.userIds.split(",").length,
      });
      modalClose();
      return;
    }
    if (selectUsers.userIds !== "") {
      setSelectUsers((prev) => ({ ...prev, next: true }));
      onNextClick({ ...selectUsers, next: true });
    } else {
      showToast(
        t("userFilter.selectAtLeastOneUser", {
          defaultValue: "Select at least one User.",
        }),
        "warning"
      );
    }
  };
  const getUsersList = () => {
    dispatch(userListGet("editUser")).then((action) =>
      middleware(action, "User List", (action) => {
        const combineUserNames = [
          ...(showShelfName && canShowSelfName
            ? [{ id: user.id, systemId: user.username }]
            : []),
          ...(action.payload[isSellerOrManager ? "usernameExt" : "usernames"] ??
            []),
        ];

        const userMap = {};
        combineUserNames.forEach((userTemp) => {
          userMap[userTemp.id] = userTemp.systemId;
        });
        setUsersList(combineUserNames);
        setFilteredUsers(combineUserNames);
      })
    );
  };
  const handleSearch = (e) => {
    let searchVal = e.target.value;
    if (searchVal && searchVal !== "" && e.type !== "blur") {
      setFilteredUsers(
        usersList.filter(
          (user) =>
            user?.systemId?.toLowerCase().includes(searchVal.toLowerCase()) ||
            (isSellerOrManager &&
              user.userEntry.systemId
                .toLowerCase()
                .includes(searchVal.toLowerCase()))
        )
      );
    } else if (searchVal == "") setFilteredUsers(usersList);
    setSearchVal(searchVal);
  };

  useEffect(() => {
    if (!isFetchingUserList && isInitialLoading) {
      setIsInitialLoading(false);
    }
  }, [isFetchingUserList, usersList, isInitialLoading]);

  useFetch(() => {
    if (isSupers) getUsersList();
  });

  return (
    <>
      {selectUsers.allowed && !selectUsers.next && (
        <Modal show={true} close={modalClose} justify="center" width={850}>
          <div className="modal-header">
            <h6 className="modal-title d-flex align-items-center gap-2 fs-22">
              <FaUsers />
              {t("userFilter.selectUsers", { defaultValue: "Select Users" })}
            </h6>
            <CloseButton theme={"dark"} onClick={modalClose} />
          </div>
          <div className="modal-body bg-white user-filter-modal-body">
            <div className="d-flex justify-content-end mb-3">
              <FormEnter onSubmit={handleSearch}>
                <div className="input-group border br-8 bg-transparent text-white">
                  <input
                    type="text"
                    className="form-control input-style border-0"
                    placeholder={t("commonUi.search", {
                      defaultValue: "Search...",
                    })}
                    value={searchVal}
                    onChange={handleSearch}
                  />
                  <button className="btn" type="submit">
                    <CiSearch className="fs-25" strokeWidth={1} />
                  </button>
                </div>
              </FormEnter>
            </div>
            {isFetchingUserList || isInitialLoading ? (
              <SkeletonComponent variant="table" rows={8} columns={3} columnWidths={["35%", "35%", "30%"]} />
            ) : (
              <div className="user-filter-table-wrapper">
                <div className="user-filter-table-scroll-area">
                  <DataTable
                    className="user-filter-table"
                    customStyles={{
                      ...tableCustomStyles({
                        height: "auto",
                        minHeight: 0,
                        overflow: "visible",
                        tableStyle: {
                          height: "auto",
                          minHeight: 0,
                          overflow: "visible",
                          border: "none",
                          borderRadius: 0,
                        },
                        wrapperStyle: {
                          height: "auto",
                          minHeight: 0,
                          overflow: "visible",
                        },
                      }),
                      responsiveWrapper: {
                        style: {
                          overflow: "visible",
                        },
                      },
                    }}
                    columns={columns}
                    data={data}
                    highlightOnHover
                    pagination={false}
                    selectableRows
                    selectableRowsSingle={!multipleUsers && !allSelected}
                    clearSelectedRows={clearSelectedRows}
                    onSelectedRowsChange={handleSelectedUserChange}
                    noDataComponent={
                      <div className="p-4">
                        {t("noData", { defaultValue: "No data available" })}
                      </div>
                    }
                  />
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer bg-white">
            <div className="row g-3 w-100">
              <div className="col-6 mt-0">
                <button
                  type="button"
                  className="btn fs-16 fw-500 br-12 w-100 px-4 py-2 border_lightgray_1 me-2"
                  onClick={handleUserSelectionReset}
                  disabled={selectUsers.userIds === ""}
                >
                  {t("commonUi.reset", { defaultValue: "Reset" })}
                </button>
              </div>
              <div className="col-6 mt-0">
                <button
                  type="button"
                  className="cbtn text-white btn w-100 fs-16 fw-500 br-12 px-4 py-2 border_lightgray_1"
                  onClick={handleNextClick}
                  disabled={
                    !multipleUsers
                      ? selectUsers.userIds === "" ||
                        selectUsers.userIds.split(",").length !== 1
                      : selectUsers.userIds === ""
                  }
                >
                  {onlyUserSelect
                    ? t("submit", { defaultValue: "Submit" })
                    : t("next", { defaultValue: "Next" })}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default UserFilter;
